<template>
  <lf-checkbox-dropdown
    class="h-10 min-w-60"
    enable-search
    name="required_documents"
    show-clear-filter
    width-full
    dropdown-width-full
    no-margin
    sort
    button-classes="'-pl-1 h-full w-full sm:pl-2"
    :options="requiredDocumentsOptions"
    :placeholder="$t('STIPS.TITLE')"
    :model-value="modelValue"
    :get-paginated-options="
      (params: Record<string, string>) => {
        return stipsApiService.getStips({
          ...params,
          selected_ids: modelValue || [],
        });
      }
    "
    @update:model-value="$emit('update:model-value', $event as number[])"
    @loaded-more="
      (value) =>
        handleRequiredDocuments(value as RequiredDocument[], 'loadmore')
    "
    @options-search="
      (value) => handleRequiredDocuments(value as RequiredDocument[], 'search')
    "
  >
    <template #header>
      <div class="pt-2 text-center font-semibold text-headline">
        {{ $t("STIPS.TITLE") }}
      </div>
      <div class="flex min-w-60 px-3 pt-2" data-cy="documents-filter-type">
        <div
          v-for="type in documentOptions"
          class="cursor-pointer border p-2"
          :data-cy="`documents-filter-type-${type.type}`"
          :key="type.type"
          :class="activeType === type.type ? 'bg-blue-50 text-blue-600' : ''"
          @click.stop="changeDocumentsCompletedFilter(type.type, type.value)"
        >
          {{ type.title }}
        </div>
      </div>
    </template>
    <template v-slot="slotProps">
      <span
        v-tooltip="{
          content: label,
          onShow: () => showLabel,
        }"
        class="mr-5 ml-3 block truncate"
        data-cy="documents-filter-label"
        :class="{
          'text-gray-400': !showLabel,
        }"
      >
        {{ showLabel ? label : t("STIPS.TITLE") }}
      </span>
      <span
        class="dropdown-icon absolute inset-y-0 right-0 ml-3 flex cursor-pointer items-center pr-2"
      >
        <icon-base
          class="transform text-gray-400 transition-all"
          height="6"
          width="10"
          icon="dropdown-arrow"
          :class="{ 'rotate-180': slotProps.showMenu }"
        />
      </span>
    </template>
  </lf-checkbox-dropdown>
</template>
<script setup lang="ts">
import { computed, ref, watch } from "vue";
import { useI18n } from "vue-i18n";

import { createOptionsUpdatedHandler } from "@/helpers/deals";
import stipsApiService from "@/services/modules/stips";

import LfCheckboxDropdown from "@/components/ui/inputs/LfCheckboxDropdown.vue";

import type { RequiredDocument } from "@/models/common";

const emit = defineEmits<{
  documentsChanged: [value: string];
  "update:model-value": [value: number[]];
}>();

const props = defineProps<{
  modelValue: string[];
  documentsCompleted: string;
}>();

const { t } = useI18n();

enum DocumentCompletionStatus {
  all = "all",
  complete = "complete",
  incomplete = "incomplete",
}

const documentOptions = [
  {
    type: DocumentCompletionStatus.all,
    title: t("DEALS.FILTERS.ALL"),
    value: "",
  },
  {
    type: DocumentCompletionStatus.complete,
    title: t("DEALS.FILTERS.COMPLETE"),
    value: "1",
  },
  {
    type: DocumentCompletionStatus.incomplete,
    title: t("DEALS.FILTERS.INCOMPLETE"),
    value: "0",
  },
];

const requiredDocumentsOptions = ref<Record<string, string>>({});
const activeType = ref(DocumentCompletionStatus.all);

const showLabel = computed(
  () =>
    props.modelValue.length || activeType.value !== DocumentCompletionStatus.all
);

const labelType = computed(
  () =>
    documentOptions.find((option) => option.type === activeType.value)?.title ||
    t("DEALS.FILTERS.ALL")
);

const label = computed(() => {
  const stips = (props.modelValue ?? []).map((type) => {
    if (requiredDocumentsOptions.value[type]) {
      return requiredDocumentsOptions.value[type];
    }
  });

  if (!stips.length) {
    return labelType.value;
  }

  return `${labelType.value}: ${stips?.join(", ") || "-"}`;
});

const handleRequiredDocuments = createOptionsUpdatedHandler(
  requiredDocumentsOptions
);

const changeDocumentsCompletedFilter = (
  type: DocumentCompletionStatus,
  value: string
) => {
  activeType.value = type;
  emit("documentsChanged", value);
};

watch(
  () => props.documentsCompleted,
  (value: string) => {
    activeType.value =
      documentOptions.find((type) => type.value == value)?.type ??
      DocumentCompletionStatus.all;
  },
  { immediate: true }
);
</script>
